import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@produzionejb/ui';
import { useConList, useCreateCon, useDeleteCon } from '../../lib/con-api';
import { XlsxExportButton } from '../../components/XlsxExportButton';

export function ConListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useConList();
  const createMut = useCreateCon();
  const deleteMut = useDeleteCon();

  async function handleCreateForToday() {
    const today = new Date().toISOString().slice(0, 10);
    try {
      const res = await createMut.mutateAsync({ workDate: today });
      navigate(`/documents/con/${res.id}/edit`);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  async function handleDelete(id: string, code: string | null) {
    if (!window.confirm(t('con.list.confirmDelete', { code: code ?? 'DRAFT' }))) return;
    try {
      await deleteMut.mutateAsync(id);
    } catch (err) {
      window.alert((err as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-semibold text-slate-900">{t('con.list.title')}</h2>
        <div className="flex items-center gap-2">
          <XlsxExportButton path="/api/documents/con?format=xlsx" fallbackFilename="con.xlsx" />
          <Button onClick={() => void handleCreateForToday()} disabled={createMut.isPending}>
            {t('con.list.newButton')}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>
            {data ? t('con.list.totalCount', { count: data.total }) : ''}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading && <p className="px-4 py-3 text-sm text-slate-500">{t('common.loading')}</p>}
          {error && <p className="px-4 py-3 text-sm text-red-700">{(error as Error).message}</p>}
          {data && data.items.length === 0 && (
            <p className="px-4 py-6 text-sm text-slate-500">{t('con.list.empty')}</p>
          )}
          {data && data.items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase text-slate-600">
                  <tr>
                    <th className="px-4 py-2">{t('con.fields.code')}</th>
                    <th className="px-4 py-2">{t('con.fields.workDate')}</th>
                    <th className="px-4 py-2">{t('con.fields.status')}</th>
                    <th className="px-4 py-2 text-right">{t('con.fields.righeCount')}</th>
                    <th className="px-4 py-2 text-right">{t('con.fields.totConsumedKg')}</th>
                    <th className="px-4 py-2 text-right">{t('con.fields.totPieces')}</th>
                    <th className="px-4 py-2 text-right">{t('con.fields.totBoxes')}</th>
                    <th className="px-4 py-2 text-right">{t('con.fields.totSfridoG')}</th>
                    <th className="px-4 py-2 text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 font-mono text-xs">{c.code ?? '—'}</td>
                      <td className="px-4 py-2">
                        {new Date(c.workDate).toLocaleDateString('it-IT')}
                      </td>
                      <td className="px-4 py-2">
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            c.status === 'CLOSED'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {c.status === 'CLOSED' ? t('con.status.closed') : t('con.status.draft')}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right text-slate-700">{c.righeCount}</td>
                      <td className="px-4 py-2 text-right text-slate-700">
                        {(Number(c.totQtyConsumedG) / 1000).toFixed(2)} kg
                      </td>
                      <td className="px-4 py-2 text-right text-slate-700">{c.totPiecesProduced}</td>
                      <td className="px-4 py-2 text-right font-semibold text-brand-700">
                        {c.totBoxesCreated}
                      </td>
                      <td className="px-4 py-2 text-right text-slate-700">
                        {Number(c.totSfridoG).toFixed(1)}g
                      </td>
                      <td className="px-4 py-2 text-right space-x-2 whitespace-nowrap">
                        <Link
                          to={`/documents/con/${c.id}/edit`}
                          className="text-xs font-medium text-brand-700 hover:underline"
                        >
                          {c.status === 'DRAFT' ? t('common.edit') : t('common.view')}
                        </Link>
                        {c.status === 'DRAFT' && (
                          <button
                            type="button"
                            onClick={() => void handleDelete(c.id, c.code)}
                            className="text-xs font-medium text-red-600 hover:underline"
                          >
                            {t('common.delete')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
